import { RefObject, useEffect, useRef, useState } from 'react';

type Measured<T extends HTMLElement> = {
  ref: RefObject<T>;
  height: number;
};

export const useMeasuredHeight = <T extends HTMLElement>(
  fallback: number,
  selector?: string,
): Measured<T> => {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState(fallback);

  useEffect(() => {
    const element = ref.current ?? (selector ? document.querySelector(selector) : null);

    if (!element) {
      return undefined;
    }

    const measure = () => setHeight(Math.ceil(element.getBoundingClientRect().height));
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(element);

    return () => observer.disconnect();
  }, [selector]);

  return { ref, height };
};
